import { format, isValid, parse } from 'date-fns'
import { FC, useContext, useMemo } from 'react'

import { AuthSessionContext } from '@context/AuthContext'

import { Button } from '@ui/components/Button'
import { Chip } from '@ui/components/Chip'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { IconFCType } from '@ui/icons/types'

import { Operator } from '@utils/ApiClient'
import { getTwoYearsAgo } from '@utils/date.utils'
import { extractValueFromFilterQuery } from '@utils/extractValueFromFilterQuery'

import { FilterQueryProps } from '@model/FilterFields'

import { useCalendarRangeOptions } from '@hooks/useCalendarRangeOptions'

import { CalendarPopup } from '../../Calendar/CalendarPopup'
import { CheckBoxProps } from './components/Range.modal'
import { FromTo } from './interfaces/fromTo.interface'

import styles from './FilterChipModalDateRange.module.scss'

export interface DateShortcutOption {
  name: string
  range: () => [Date, Date]
}

interface FilterChipModalDateRangeProps {
  placeholders?: FromTo
  name: string
  keys: FromTo
  state?: FilterQueryProps
  isQueryParameter?: boolean
  shortcutOptions?: DateShortcutOption[]
  onSelect?: (value: FilterQueryProps) => void
  inputIcon?: IconFCType
  allowFutureDates?: boolean
  checkBox?: CheckBoxProps
  size?: 'sm' | 'md' | 'lg'
  openExportModal?: () => void
}

const DATE_FORMAT = 'MM/dd/yyyy HH:mm:ss'
const DATE_FORMAT_NO_TIME = 'MM/dd/yyyy'

export const FilterChipModalDateRange: FC<FilterChipModalDateRangeProps> = (props) => {
  const {
    name,
    keys,
    state,
    isQueryParameter,
    onSelect,
    allowFutureDates,
    checkBox,
    size = 'md',
    openExportModal,
  } = props

  const [auth] = useContext(AuthSessionContext)

  const dateFormat = auth?.dateFormatType?.name

  const calendarRangeOptions = useCalendarRangeOptions()

  const getValueBasedOnQueryParameter = (operator: Operator, value?: Date) => {
    if (!value || !dateFormat) {
      return undefined
    }
    const formattedValue = format(value, DATE_FORMAT)

    return isQueryParameter
      ? formattedValue
      : {
          value: formattedValue,
          operator: operator,
        }
  }

  const handleSelect = (values: FilterQueryProps) => {
    onSelect?.({
      ...state,
      [keys.from]: values.from,
      [keys.to]: values.to,
      ...(checkBox ? { [checkBox.key]: values[checkBox.key] } : {}),
    })
  }

  const initialValues: FilterQueryProps = {
    [keys.from]: extractValueFromFilterQuery(state?.[keys.from]) ?? '',
    [keys.to]: extractValueFromFilterQuery(state?.[keys.to]) ?? '',
    ...(checkBox ? { [checkBox.key]: state?.[checkBox.key] } : {}),
  }

  const parseDate = (date?: string) => {
    if (!date?.length) {
      return undefined
    }

    const extractedValue = extractValueFromFilterQuery(date)

    let parsedDate = parse(extractedValue, DATE_FORMAT, new Date())

    if (!isValid(parsedDate)) {
      parsedDate = parse(extractedValue, DATE_FORMAT_NO_TIME, new Date())
    }

    if (!isValid(parsedDate)) {
      return undefined
    }

    return parsedDate
  }

  const handleCustomDateRangeSelect = (range: [Date, Date], checkVal?: string): void => {
    const startDate = range[0]
    const endDate = new Date(range[1])
    endDate.setHours(23, 59, 59, 999)

    handleSelect({
      from: getValueBasedOnQueryParameter(Operator.MORE_OR_EQUAL, startDate),
      to: getValueBasedOnQueryParameter(Operator.LESS_OR_EQUAL, endDate),
      ...(checkBox ? { [checkBox.key]: checkVal } : {}),
    })
  }

  const countText = useMemo<string>(() => {
    let count = 0
    if (state?.[keys.from]) count++
    if (state?.[keys.to]) count++
    if (checkBox && state?.[checkBox.key]) count++
    return count > 0 ? String(count) : ''
  }, [checkBox, keys.from, keys.to, state])

  return (
    <>
      <CalendarPopup
        onConfirm={handleCustomDateRangeSelect}
        rangeOptions={calendarRangeOptions}
        startDate={parseDate(state?.[keys.from] as string)}
        endDate={parseDate(state?.[keys.to] as string)}
        checkBox={checkBox}
        initialValues={initialValues}
        popupWrapperClassName={styles.popupWrapperOverwrite}
        placement='bottom'
        label={name}
        minDate={openExportModal ? getTwoYearsAgo() : undefined}
        maxDate={allowFutureDates ? undefined : new Date()}
        monthsShown={2}
        showEarlyDateLimitReason={!!openExportModal}
        openExportModal={openExportModal}
      >
        <Button.Secondary
          size={size}
          iconRight={ChevronIcon.Down}
          endDecorator={
            (state?.[keys.from] || state?.[keys.to] || (checkBox && state?.[checkBox.key])) && (
              <Chip
                selected
                size='xs'
                onDelete={(event) => {
                  event.stopPropagation()
                  event.preventDefault()
                  onSelect?.({
                    ...state,
                    [keys.from]: undefined,
                    [keys.to]: undefined,
                    ...(checkBox ? { [checkBox.key]: undefined } : {}),
                  })
                }}
                key={`${keys.from}-${keys.to}`}
              >
                {countText}
              </Chip>
            )
          }
        >
          {name}
        </Button.Secondary>
      </CalendarPopup>
    </>
  )
}
